// Analytics controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import type { Snapshot } from '../../core';
import { PALETTE } from '../keys';
import { track, type AnalyticsDims } from '../../analytics/client';
import { PLAYER, AI } from './types';
import type { GameScene } from '../scenes/GameScene';



export function analyticsDims(this: GameScene, snap?: Snapshot, extra: AnalyticsDims = {}): AnalyticsDims {
    const elapsedMs =
      snap !== undefined ? Math.max(0, Math.floor(snap.timeMs - this.boardStartSimMs)) : undefined;
    return {
      boardSource: this.boardSource,
      topicCategory: this.topic,
      difficulty: this.boardIndex + 1,
      boardIndex: this.boardIndex,
      elapsedMs,
      ...extra,
    };
  }


export function trackBoardCompleted(this: GameScene, snap: Snapshot): void {
    if (this.boardTerminalTracked) return;
    this.boardTerminalTracked = true;
    const won = snap.status === 'won';
    const human = snap.players.find((p) => p.id === PLAYER);
    const failureReason = won
      ? undefined
      : human && human.lives <= 0
        ? 'lives'
        : snap.timeLeftMs <= 0
          ? 'timer'
          : snap.winner === AI
            ? 'rival'
            : 'unknown';
    track(
      'board_completed',
      this.analyticsDims(snap, {
        result: won ? 'won' : 'lost',
        failureReason,
      }),
    );
  }


  /**
   * Gentle unstick hint for stalled players on novice/training boards (the only boards calm enough to
   * stall on). Fires after 9s without any input, at most 3 times per board, and points at the VERBS
   * (move / SCAN / CHOMP) — never at which tile is correct (no-pre-reveal pillar).
   */
export function maybeIdleNudge(this: GameScene, snap: Snapshot): void {
    if (!this.noviceRun || this.preRoundActive || this.ended || this.paused) return;
    if (snap.status !== 'playing') return;
    // Doomed-race notice (independent of idleness — a novice racing pointlessly needs it too).
    if (!this.doomedNoticeShown) {
      const human = snap.players.find((p) => p.id === PLAYER);
      if (human) {
        const toGo = Math.max(0, snap.quota - human.eatenCorrect);
        const uneatenCorrect = snap.cells.reduce((n, c) => n + (c.kind === 'correct' && !c.eaten ? 1 : 0), 0);
        if (toGo > 0 && uneatenCorrect < toGo) {
          this.doomedNoticeShown = true;
          this.showCommandHint('The rival ate too many — this board is theirs. Next one is yours!', PALETTE.armed, 6000);
        }
      }
    }
    if (this.idleNudgesShown >= 3) return;
    const now = this.time.now;
    if (this.lastHumanInputMs === 0) {
      this.lastHumanInputMs = now; // arm on the first live frame so the GET READY hold never counts
      return;
    }
    if (now - this.lastHumanInputMs < 9000) return;
    this.lastHumanInputMs = now;
    this.idleNudgesShown += 1;
    const human = snap.players.find((p) => p.id === PLAYER);
    const canScan = this.scanUnlocked && (human?.scansLeft ?? 0) > 0;
    this.showCommandHint(
      canScan
        ? 'Stuck? SCAN reveals the tiles around you — then CHOMP one that fits the rule.'
        : 'Tap any tile to walk there (or swipe/arrows) — then CHOMP a tile that fits the rule.',
      PALETTE.scan,
    );
  }


export function trackBoardAbandoned(this: GameScene): void {
    if (this.boardTerminalTracked || !this.sim) return;
    const snap = this.sim.snapshot();
    if (snap.status !== 'playing') return;
    this.boardTerminalTracked = true;
    track('board_abandoned', this.analyticsDims(snap, { result: 'abandoned' }));
  }
